import axios from '../utils/axios';

/**
 * Feedback reports and their attachments.
 *
 * Attachment upload is a three-step handshake so image bytes never pass
 * through the API server: ask for a presigned POST, upload straight to
 * S3, then confirm. The server HEADs the object on confirm, so a client
 * that skips the upload cannot mark an attachment as present.
 */

export interface FeedbackAttachment {
  id: string;
  s3_key: string;
  file_name?: string | null;
  content_type: string;
  size_bytes?: number | null;
  uploaded_at?: string | null;
  download_url?: string | null;
}

export interface FeedbackReport {
  id: string;
  user_id: string;
  team_id?: string | null;
  feedback_type: number;
  product_area: number;
  status: number;
  title: string;
  ticket_ref?: string | null;
  // Resolved server-side from user_id / team_id. Null once the account or
  // team behind a report is gone.
  user_name?: string | null;
  team_name?: string | null;
  created_at: string;
  updated_at: string;
  // Present only on a single-report read (FeedbackTypeFull).
  body?: string;
  context?: Record<string, unknown> | null;
  resolution_note?: string | null;
  processed_at?: string | null;
  attachments?: FeedbackAttachment[];
}

export interface NewFeedbackReport {
  feedback_type: number;
  product_area: number;
  title: string;
  body: string;
  context?: Record<string, unknown> | null;
}

interface PresignedUpload {
  url: string;
  fields: Record<string, string>;
}

interface AllocatedAttachment {
  attachment_id: string;
  s3_key: string;
  upload: PresignedUpload;
}

/**
 * Human-readable detail from a failed feedback API call. The server's
 * error envelope puts its message at `response.data.error`; this is the
 * one place that shape is known on the client.
 */
export function feedbackErrorDetail(error: any): string {
  return error?.response?.data?.error ?? error?.message ?? 'Unknown error';
}

export async function createFeedback(report: NewFeedbackReport): Promise<FeedbackReport> {
  return axios.post('/feedback', report).then((res) => res.data);
}

export async function getFeedbackReports(params: Record<string, string> = {}): Promise<FeedbackReport[]> {
  return axios.get('/feedback', { params }).then((res) => res.data);
}

export async function getFeedbackReport(id: string): Promise<FeedbackReport> {
  return axios.get(`/feedback/${id}`).then((res) => res.data);
}

/** Triage-only: status, ticket reference, resolution note. */
export async function patchFeedbackReport(id: string, changes: Partial<FeedbackReport>): Promise<FeedbackReport> {
  return axios.patch(`/feedback/${id}`, { id, ...changes }).then((res) => res.data);
}

async function allocateAttachments(feedbackId: string, files: File[]): Promise<AllocatedAttachment[]> {
  const payload = files.map((file) => ({ content_type: file.type, file_name: file.name }));
  return axios.post(`/feedback/${feedbackId}/attachments`, { files: payload }).then((res) => res.data);
}

/**
 * POST one file to S3 with its presigned form.
 *
 * Uses bare `fetch`, not the app's axios instance: the interceptor
 * attaches an Authorization header, and S3 rejects a presigned POST that
 * also carries one. The presign's own signature is the authorisation.
 */
async function uploadToS3(upload: PresignedUpload, file: File): Promise<void> {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => form.append(key, value));
  // The file part must come last — S3 ignores anything after it.
  form.append('file', file);

  const response = await fetch(upload.url, { method: 'POST', body: form });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status}) for ${file.name}`);
  }
}

async function confirmAttachment(feedbackId: string, attachmentId: string): Promise<void> {
  await axios.put(`/feedback/${feedbackId}/attachments/${attachmentId}`);
}

/**
 * Upload every attachment for a report.
 *
 * Returns the names of files that failed rather than throwing: by this
 * point the report itself is saved, and losing a screenshot is much less
 * bad than telling the user their whole report failed. The caller
 * surfaces the names so they know what did not arrive.
 */
export async function uploadFeedbackAttachments(feedbackId: string, files: File[]): Promise<string[]> {
  if (!files.length) return [];

  // The presign step is inside the contract too: it fails as a whole (a
  // missing bucket config, say), and letting that reject would tell the
  // user their report failed when the row is already saved — so they file
  // it again. Every file it covers is reported as a failed attachment
  // instead.
  let allocated: AllocatedAttachment[];
  try {
    allocated = await allocateAttachments(feedbackId, files);
  } catch (error) {
    console.error('Feedback attachment allocation failed:', error);
    return files.map((file) => file.name);
  }

  const failures: string[] = [];

  await Promise.all(
    allocated.map(async (item, index) => {
      const file = files[index];
      try {
        await uploadToS3(item.upload, file);
        await confirmAttachment(feedbackId, item.attachment_id);
      } catch (error) {
        console.error(`Feedback attachment ${file?.name} failed:`, error);
        failures.push(file?.name ?? item.s3_key);
      }
    }),
  );
  return failures;
}
